import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { formatNumberToUSD, calculateTermPmt } from 'src/shared/calculators';
import { showErrorStatus } from 'src/security/state/user-slice';
import { parseAmount, formatCurrencyInput, stripCurrencyInput } from '../hub-utils';
import type { PaymentProgramViewContext } from './use-payment-program-view';

/* UX and program calculation rules:
    - All calculated programs must satisfy the Program Equation: pfrAmt - down payment - (terms × recurring) = 0 within .50
    - Terms must be either a multiple of 3 months, or a multiple of 12 weeks.
    - Terms should be the lowest that will satisfy the Program Equation.
    - Down payments should be maximized.
    - The minimum preauthorized recurring payment is the min recurring amount calculated when the highest preauthorized term is applied.
    - The minimum preauthorized down payment is the down payment amount calculated when the applicable risk rule for the pfr is applied.
    - If down payment only is selected, the recurring payment is at most the optimal recurring amount (never above it);
        it may be lower per period when needed so the program equation balances with allowed terms. Down payment
        can decrease from the user's entry. Terms can snap up or down depending on the valid term values.
    - If recurring payment only is selected, the down payment is fixed to the optimal down payment amount. Recurring payment
        can decrease from the user's entry.  Terms can snap up or down depending on the valid term values.
    - If down payment and recurring payment are both selected, the down payment and recurring payment
        can decrease from the user's entry.  Terms can snap up or down depending on the valid term values.
    - Program calculation or option selection should not change the user's entered data in the affordable down or recurring
        payment fields.
    - The program options section will always show the calculated program based on the rules above.
    - If the calculated program is high risk, also show the minimal preauthorized program.
    - Preauthorized program options should be shown with a green surrounding box.
    - High risk program options should be shown with a red surrounding box.
    - If the user entered down payment amount is less than the minimal down payment, show the min preauthorized down
        payment prompt.
    - If the user entered recurring payment amount is less than the minimal recurring payment, show the min preauthorized
        recurring payment prompt (threshold uses affordable down when collected, else rule min down).
    - The calculated program down payment or recurring payment can never be greater than the PFR amount.
    - If a high risk program option is selected show Process HRE button.
    - If a preauthorized program option is selected show Add Payment Details button.
*/

/** Snap term (months) to allowed values: 0, 1, or a multiple of 3. */
function snapTermToAllowed(termInMonths: number): number {
  if (termInMonths <= 0) return 0;
  if (termInMonths <= 1.5) return 1;
  return Math.round(termInMonths / 3) * 3;
}

/** Smallest allowed term (0, 1, or multiple of 3) that is >= x. */
function snapTermUpToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x <= 1) return 1;
  return Math.ceil(x / 3) * 3;
}

/** Snap term in weeks to allowed values: 0, 1, 2, 3, or a multiple of 12. */
function snapWeeksToAllowed(weeks: number): number {
  if (weeks <= 0) return 0;
  if (weeks <= 1.5) return 1;
  if (weeks <= 2.5) return 2;
  if (weeks <= 7.5) return 3;
  return Math.round(weeks / 12) * 12;
}

/** Smallest allowed weeks (0, 1, 2, 3, or multiple of 12) that is >= x. */
function snapWeeksUpToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x <= 1) return 1;
  if (x <= 2) return 2;
  if (x <= 3) return 3;
  return Math.ceil(x / 12) * 12;
}

/** Largest allowed term (months: 0, 1, or multiple of 3) that is <= x. */
function snapTermDownToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x < 1.5) return 1;
  return Math.floor(x / 3) * 3;
}

/** Largest allowed weeks (0, 1, 2, 3, or multiple of 12) that is <= x. */
function snapWeeksDownToAllowed(x: number): number {
  if (x <= 0) return 0;
  if (x < 1.5) return 1;
  if (x < 2.5) return 2;
  if (x < 7.5) return 3;
  return Math.floor(x / 12) * 12;
}

/** Tolerance for pfrAmt - dp - (term × recurring) ≈ 0 (in dollars). Rule: within .50 */
const BALANCE_TOLERANCE = 0.5;

/**
 * Context slice that the custom hook adds. Base hook must provide the rest of PaymentProgramViewContext.
 */
export interface CustomProgramContextAddition {
  customDownPayment: string;
  setCustomDownPayment: (v: string) => void;
  customTerm: string;
  setCustomTerm: (v: string) => void;
  customRecurringAmount: string;
  setCustomRecurringAmount: (v: string) => void;
  downPaymentError: string;
  recurringAmountError: string;
  isHighRisk: boolean;
  /** Min preauthorized down (rule min down when highest preauthorized term is applied). */
  minDownPaymentPreapproved: number | null;
  /** Min preauthorized recurring when highest preauthorized term is applied. */
  minRecurringPreapproved: number | null;
  /** Min recurring at max term: affordable DP when DP field shown, else rule min down (prompt + preauth / high-risk checks). */
  minRecurringPreapprovedForPrompt: number | null;
  /** Highest preauthorized term (months). */
  minTermPreapproved: number | null;
  customDownPmt: number;
  customTermValue: number;
  customRecurringAmtValue: number;
  calculatedTerm: number;
  calculatedTermDisplay: number;
  customRecurringAmtFinal: number;
  handleDownPaymentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRecurringAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDownPaymentBlur: () => void;
  handleRecurringAmountBlur: () => void;
  handleSelectCustomProgram: () => void;
  handleSelectMinPreapprovedProgram: () => void;
  handleProcessHre: () => void;
  /** True when calculated program satisfies preauth mins (uses affordable-down-based recurring floor when applicable). */
  meetsPreauthMinimums: boolean;
}

/**
 * Custom program–only state, derived values, and handlers.
 * Call with the result of usePaymentProgramView (base) and merge: { ...base, ...useCustomProgramView(base) }.
 */
export function useCustomProgramView(
  base: Omit<PaymentProgramViewContext, keyof CustomProgramContextAddition>
): CustomProgramContextAddition {
  const location = useLocation();
  const {
    pfrAmt,
    matchingRule,
    optDownPayment,
    recurringAmt,
    billingFrequency,
    showDownPayment,
    setShowDownPayment,
    showRecurringAmount,
    setShowRecurringAmount,
    setShowFields,
    createProgramAndNavigate,
    navigate,
    getProgramPath,
    programType,
    patientId,
    encounterId,
    isCustomResults,
    dispatch,
    hreApprovalStatus,
    setBillingFrequency,
    activeEncounter,
  } = base;

  // Min preapproved amounts (used when highest preauthorized term is applied)
  const { minDownPaymentPreapproved, minRecurringPreapproved, minTermPreapproved } = useMemo(() => {
    const minDown: number | null =
      matchingRule && (matchingRule.minDownPmt != null || matchingRule.minDownPmt === 0)
        ? matchingRule.minDownPmtType === '%'
          ? ((matchingRule.minDownPmt || 0) * pfrAmt) / 100
          : matchingRule.minDownPmt ?? 0
        : null;
    const availableTermsArr =
      matchingRule?.availableTerms?.length > 0
        ? matchingRule.availableTerms.replace(' ', '').split(',').map((t: string) => +t).filter((n: number) => !isNaN(n) && n > 0)
        : [];
    const largestTerm = availableTermsArr.length > 0 ? Math.max(...availableTermsArr) : null;
    const minRecurring: number | null =
      minDown != null && largestTerm != null && largestTerm > 0
        ? billingFrequency === 'weekly'
          ? (pfrAmt - minDown) / (largestTerm * 4)
          : calculateTermPmt(pfrAmt, minDown, largestTerm)
        : null;
    const minTerm: number | null = largestTerm;
    return {
      minDownPaymentPreapproved: minDown,
      minRecurringPreapproved: minRecurring,
      minTermPreapproved: minTerm,
    };
  }, [matchingRule, pfrAmt, billingFrequency]);

  const [customDownPayment, setCustomDownPayment] = useState<string>('');
  const [customTerm, setCustomTerm] = useState<string>('');
  const [customRecurringAmount, setCustomRecurringAmount] = useState<string>('');
  const [downPaymentError, setDownPaymentError] = useState<string>('');
  const [recurringAmountError, setRecurringAmountError] = useState<string>('');
  const minRecurringPreapprovedForPrompt = useMemo(() => {
    const largestTerm = minTermPreapproved;
    if (largestTerm == null || largestTerm <= 0 || pfrAmt <= 0) return null;

    let effectiveDown: number;
    if (showDownPayment) {
      if (customDownPayment) {
        const parsed = parseAmount(customDownPayment);
        effectiveDown = !isNaN(parsed) ? parsed : optDownPayment || 0;
      } else {
        effectiveDown = optDownPayment || 0;
      }
    } else {
      if (minDownPaymentPreapproved == null) return null;
      effectiveDown = minDownPaymentPreapproved;
    }
    effectiveDown = Math.min(Math.max(0, effectiveDown), pfrAmt);
    const remainder = pfrAmt - effectiveDown;
    if (remainder <= 0) return null;

    return billingFrequency === 'weekly'
      ? remainder / (largestTerm * 4)
      : calculateTermPmt(pfrAmt, effectiveDown, largestTerm);
  }, [
    minTermPreapproved,
    minDownPaymentPreapproved,
    pfrAmt,
    billingFrequency,
    showDownPayment,
    customDownPayment,
    optDownPayment,
  ]);

  const defaultsSetForRouteRef = useRef<string>('');
  const prevBillingFrequencyRef = useRef<'monthly' | 'weekly'>(billingFrequency);
  /** Encounter key we already populated HRE amendment fields from (when landing without state) */
  const hrePopulatedFromEncounterKeyRef = useRef<string>('');

  // Set defaults for custom route (first step: programType === 'custom' && !isCustomResults)
  useEffect(() => {
    const currentRoute = `${programType}-${isCustomResults}`;
    if (defaultsSetForRouteRef.current === currentRoute) return;
    if (programType === 'custom' && !isCustomResults) {
      setShowFields(false);
      const currentOptDownPayment =
        matchingRule?.optimalDownPmtType === '%'
          ? ((matchingRule.optimalDownPmt || 0) * pfrAmt) / 100
          : matchingRule?.optimalDownPmt || 0;
      if (currentOptDownPayment > 0) setCustomDownPayment(formatCurrencyInput(currentOptDownPayment));
      if (matchingRule?.optimalTerm) setCustomTerm(matchingRule.optimalTerm.toString());
      const optTermNum = matchingRule?.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
      const optTermDivisor = billingFrequency === 'weekly' ? optTermNum * 4 : optTermNum;
      const optimalRecurringAmt =
        optTermDivisor > 0 && pfrAmt > currentOptDownPayment
          ? (pfrAmt - currentOptDownPayment) / optTermDivisor
          : 0;
      if (optimalRecurringAmt > 0) {
        setCustomRecurringAmount(formatCurrencyInput(optimalRecurringAmt));
        prevBillingFrequencyRef.current = billingFrequency;
      }
      defaultsSetForRouteRef.current = currentRoute;
    } else if (isCustomResults) {
      setShowFields(true);
      defaultsSetForRouteRef.current = currentRoute;
    }
  }, [programType, isCustomResults, matchingRule, pfrAmt, billingFrequency, setShowFields]);

  // When billing frequency changes on custom flow, convert recurring amount
  useEffect(() => {
    if (prevBillingFrequencyRef.current === billingFrequency) return;
    const prev = prevBillingFrequencyRef.current;
    prevBillingFrequencyRef.current = billingFrequency;
    const isCustomFlow = programType === 'custom' || isCustomResults;
    if (!isCustomFlow || !customRecurringAmount) return;
    const val = parseAmount(customRecurringAmount);
    if (isNaN(val) || val <= 0) return;
    if (prev === 'monthly' && billingFrequency === 'weekly') {
      setCustomRecurringAmount(formatCurrencyInput(val / 4));
    } else if (prev === 'weekly' && billingFrequency === 'monthly') {
      setCustomRecurringAmount(formatCurrencyInput(val * 4));
    }
  }, [billingFrequency, programType, isCustomResults, customRecurringAmount]);

  // Custom-results: apply checkbox state from navigation and prime amounts
  useEffect(() => {
    if (!isCustomResults || patientId <= 0 || encounterId <= 0) return;
    const navState = location.state as { showDownPayment?: boolean; showRecurringAmount?: boolean } | null;
    const fromCustom = navState && (navState.showDownPayment === true || navState.showRecurringAmount === true);
    if (fromCustom) {
      if (navState.showDownPayment) setShowDownPayment(true);
      if (navState.showRecurringAmount) setShowRecurringAmount(true);
      setShowFields(true);
      const currentOptDownPayment =
        matchingRule?.optimalDownPmtType === '%'
          ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
          : matchingRule?.optimalDownPmt || 0;
      if (currentOptDownPayment > 0) setCustomDownPayment(formatCurrencyInput(currentOptDownPayment));
      if (matchingRule?.optimalTerm) setCustomTerm(matchingRule.optimalTerm.toString());
      const optTermNum = matchingRule?.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
      const optTermDivisor = billingFrequency === 'weekly' ? optTermNum * 4 : optTermNum;
      const optimalRecurringAmt =
        optTermDivisor > 0 && pfrAmt > currentOptDownPayment
          ? (pfrAmt - currentOptDownPayment) / optTermDivisor
          : 0;
      if (optimalRecurringAmt > 0) setCustomRecurringAmount(formatCurrencyInput(optimalRecurringAmt));
      return;
    }
    navigate(getProgramPath(`/hub/patient/${patientId}/ioc/${encounterId}/program/custom`), { replace: true });
  }, [isCustomResults, patientId, encounterId, location.state, navigate, getProgramPath, matchingRule, pfrAmt, billingFrequency, setShowDownPayment, setShowRecurringAmount, setShowFields]);

  // HRE approval: restore custom program state from navigation (when we have hreProgramValues in state)
  useEffect(() => {
    if (programType !== 'hre-approval' || !location.state) return;
    const navState = location.state as { hreProgramValues?: { downPmtAmt: number; terms: number; recurringAmt: number; billingFrequency: 'monthly' | 'weekly' } } | null;
    const values = navState?.hreProgramValues;
    if (!values) return;
    setShowDownPayment(true);
    setShowRecurringAmount(true);
    setShowFields(true);
    setCustomDownPayment(formatCurrencyInput(values.downPmtAmt));
    setCustomTerm(values.terms.toString());
    setCustomRecurringAmount(formatCurrencyInput(values.recurringAmt));
    setBillingFrequency(values.billingFrequency);
    prevBillingFrequencyRef.current = values.billingFrequency;
    defaultsSetForRouteRef.current = `${programType}-${isCustomResults}`;
  }, [programType, isCustomResults, location.state, setShowDownPayment, setShowRecurringAmount, setShowFields, setBillingFrequency]);

  // Clear HRE defaults ref when leaving hre-approval so we can repopulate when returning
  useEffect(() => {
    if (programType !== 'hre-approval') {
      defaultsSetForRouteRef.current = '';
      hrePopulatedFromEncounterKeyRef.current = '';
    }
  }, [programType]);

  // HRE approval (custom view): when landing without state, populate amendment fields from get PE response (activeEncounter from base)
  useEffect(() => {
    if (programType !== 'hre-approval') return;
    const navState = location.state as { hreProgramValues?: unknown } | null;
    if (navState?.hreProgramValues) return; // already handled by navigation-state effect above
    const program = activeEncounter?.patientPaymentProgram?.[0];
    const schedule = program?.patientPaymentSchedule;
    if (!program || !schedule) return;
    const encounterKey = `${activeEncounter?.patientId ?? ''}-${activeEncounter?.patientEncounterId ?? ''}`;
    if (hrePopulatedFromEncounterKeyRef.current === encounterKey) return;
    hrePopulatedFromEncounterKeyRef.current = encounterKey;
    const downPmtAmt = program.downPmtAmt ?? schedule.downPmtAmt ?? 0;
    const scheduleAny = schedule as unknown as Record<string, unknown>;
    const rawPaymentDue = schedule.paymentDueAmt ?? scheduleAny?.paymentDueAmt;
    const paymentDueAmt =
      typeof rawPaymentDue === 'number' && !Number.isNaN(rawPaymentDue)
        ? rawPaymentDue
        : Number(rawPaymentDue) || 0;
    const termsRaw = schedule.terms ?? (scheduleAny?.terms as number) ?? 0;
    const isWeekly = schedule.paymentFreq === 'W' || schedule.paymentFreq === 'weekly';
    const termsInMonths = isWeekly && termsRaw > 0 ? termsRaw / 4 : termsRaw;
    const freq: 'monthly' | 'weekly' = isWeekly ? 'weekly' : 'monthly';
    setShowDownPayment(true);
    setShowRecurringAmount(true);
    setShowFields(true);
    if (downPmtAmt > 0) setCustomDownPayment(formatCurrencyInput(downPmtAmt));
    if (termsInMonths > 0) setCustomTerm(String(Math.round(termsInMonths)));
    setCustomRecurringAmount(formatCurrencyInput(paymentDueAmt));
    setBillingFrequency(freq);
    prevBillingFrequencyRef.current = freq;
  }, [programType, location.state, activeEncounter, setShowDownPayment, setShowRecurringAmount, setShowFields, setBillingFrequency]);

  const handleDownPaymentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = stripCurrencyInput(e.target.value);
      const value = parseAmount(raw);
      if (!isNaN(value) && value > pfrAmt) {
        setDownPaymentError(`Down payment cannot exceed ${formatNumberToUSD(pfrAmt)}`);
        setCustomDownPayment(formatCurrencyInput(pfrAmt));
      } else {
        setCustomDownPayment(raw);
        if (downPaymentError) setDownPaymentError('');
      }
    },
    [downPaymentError, pfrAmt]
  );

  const handleRecurringAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = stripCurrencyInput(e.target.value);
      setCustomRecurringAmount(raw);
      if (recurringAmountError) setRecurringAmountError('');
      const recurringAmtVal = parseAmount(raw);
      if (recurringAmtVal > 0 && pfrAmt > 0) {
        const currentOptDownPayment =
          matchingRule?.optimalDownPmtType === '%'
            ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
            : matchingRule?.optimalDownPmt || 0;
        const downPmt =
          showDownPayment && customDownPayment ? parseAmount(customDownPayment) : currentOptDownPayment || 0;
        const remainingPfr = pfrAmt - (showDownPayment ? downPmt : currentOptDownPayment);
        if (showRecurringAmount && !showDownPayment && recurringAmtVal > remainingPfr) {
          setRecurringAmountError(
            `Recurring cannot exceed ${formatNumberToUSD(remainingPfr)} (remaining after down payment)`
          );
          setCustomRecurringAmount(formatCurrencyInput(remainingPfr));
        }
        const effectiveRecurring =
          showRecurringAmount && !showDownPayment && recurringAmtVal > remainingPfr
            ? remainingPfr
            : recurringAmtVal;
        if (pfrAmt > downPmt && effectiveRecurring > 0) {
          const remaining = pfrAmt - (showDownPayment ? downPmt : currentOptDownPayment);
          // Snap up term so required recurring (remaining/term) <= affordable
          const termInPeriods = Math.ceil(remaining / effectiveRecurring);
          const termForStorage =
            billingFrequency === 'weekly'
              ? snapWeeksUpToAllowed(termInPeriods) / 4
              : snapTermUpToAllowed(termInPeriods);
          setCustomTerm(termForStorage.toString());
        }
      }
    },
    [
      recurringAmountError,
      pfrAmt,
      showDownPayment,
      showRecurringAmount,
      customDownPayment,
      matchingRule,
      billingFrequency,
    ]
  );

  const handleDownPaymentBlur = useCallback(() => {
    let finalValue = parseAmount(customDownPayment);
    if (!isNaN(finalValue) && finalValue > pfrAmt) {
      setDownPaymentError(`Down payment cannot exceed ${formatNumberToUSD(pfrAmt)}`);
      finalValue = pfrAmt;
      setCustomDownPayment(formatCurrencyInput(pfrAmt));
    } else {
      setDownPaymentError('');
      if (!isNaN(finalValue) && finalValue > 0) {
        setCustomDownPayment(formatCurrencyInput(finalValue));
      }
    }
    if (showRecurringAmount && customRecurringAmount) {
      const recurringAmtVal = parseAmount(customRecurringAmount);
      if (recurringAmtVal > 0 && pfrAmt > finalValue) {
        // Snap up term so required recurring <= affordable
        const termInPeriods = Math.ceil((pfrAmt - finalValue) / recurringAmtVal);
        const termForStorage =
          billingFrequency === 'weekly'
            ? snapWeeksUpToAllowed(termInPeriods) / 4
            : snapTermUpToAllowed(termInPeriods);
        setCustomTerm(termForStorage.toString());
      }
    } else if (showDownPayment && !showRecurringAmount && recurringAmt > 0 && pfrAmt > finalValue) {
      // Only affordable DP: snap term so implied dp (pfrAmt - term×recurring) <= entered dp
      const minTermInPeriods = Math.ceil((pfrAmt - finalValue) / recurringAmt);
      const termForStorage =
        billingFrequency === 'weekly'
          ? snapWeeksUpToAllowed(minTermInPeriods) / 4
          : snapTermUpToAllowed(minTermInPeriods);
      setCustomTerm(termForStorage.toString());
    }
  }, [customDownPayment, customRecurringAmount, showRecurringAmount, showDownPayment, pfrAmt, billingFrequency, recurringAmt]);

  const handleRecurringAmountBlur = useCallback(() => {
    let finalValue = parseAmount(customRecurringAmount);
    const currentOptDownPayment =
      matchingRule?.optimalDownPmtType === '%'
        ? ((matchingRule?.optimalDownPmt || 0) * pfrAmt) / 100
        : matchingRule?.optimalDownPmt || 0;
    const remainingPfr = pfrAmt - currentOptDownPayment;
    if (showRecurringAmount && !showDownPayment && !isNaN(finalValue) && finalValue > remainingPfr) {
      setRecurringAmountError(
        `Recurring cannot exceed ${formatNumberToUSD(remainingPfr)} (remaining after down payment)`
      );
      finalValue = remainingPfr;
      setCustomRecurringAmount(formatCurrencyInput(remainingPfr));
    } else if (!isNaN(finalValue) && finalValue > pfrAmt) {
      setRecurringAmountError(`Recurring amount cannot exceed ${formatNumberToUSD(pfrAmt)}`);
      setCustomRecurringAmount(formatCurrencyInput(pfrAmt));
      finalValue = pfrAmt;
    } else {
      setRecurringAmountError('');
      if (!isNaN(finalValue) && finalValue > 0) {
        setCustomRecurringAmount(formatCurrencyInput(finalValue));
      }
    }
    const downPmt =
      showDownPayment && customDownPayment ? parseAmount(customDownPayment) : currentOptDownPayment || 0;
    if (finalValue > 0 && pfrAmt > downPmt) {
      // Snap up term so required recurring <= affordable
      const termInPeriods = Math.ceil((pfrAmt - downPmt) / finalValue);
      const termForStorage =
        billingFrequency === 'weekly'
          ? snapWeeksUpToAllowed(termInPeriods) / 4
          : snapTermUpToAllowed(termInPeriods);
      setCustomTerm(termForStorage.toString());
    }
  }, [customRecurringAmount, customDownPayment, showDownPayment, showRecurringAmount, matchingRule, pfrAmt, billingFrequency]);

  const initialDownPmt =
    showDownPayment && customDownPayment ? parseAmount(customDownPayment) : optDownPayment || 0;
  const customTermValue = parseFloat(customTerm) || 0;
  const customRecurringAmtValue =
    showRecurringAmount && customRecurringAmount ? parseAmount(customRecurringAmount) : 0;

  let calculatedTerm = customTermValue;
  // When only affordable DP: recurring is locked; snap term so implied dp (pfrAmt - term×recurring) <= entered dp
  if (showDownPayment && !showRecurringAmount && recurringAmt > 0 && pfrAmt > initialDownPmt) {
    const minTermInPeriods = Math.ceil((pfrAmt - initialDownPmt) / recurringAmt);
    calculatedTerm =
      billingFrequency === 'weekly'
        ? snapWeeksUpToAllowed(minTermInPeriods) / 4
        : snapTermUpToAllowed(minTermInPeriods);
  } else if (calculatedTerm === 0 && customRecurringAmtValue > 0 && pfrAmt > initialDownPmt) {
    const termInPeriods = Math.ceil((pfrAmt - initialDownPmt) / customRecurringAmtValue);
    calculatedTerm =
      billingFrequency === 'weekly'
        ? snapWeeksUpToAllowed(termInPeriods) / 4
        : snapTermUpToAllowed(termInPeriods);
  }
  if (calculatedTerm > 0) {
    calculatedTerm =
      billingFrequency === 'weekly'
        ? snapWeeksToAllowed(calculatedTerm * 4) / 4
        : snapTermToAllowed(calculatedTerm);
  }

  // If affordable (user recurring) < calc recurring for current term, snap term up so required payment is affordable
  if (
    showRecurringAmount &&
    customRecurringAmtValue > 0 &&
    pfrAmt > initialDownPmt &&
    calculatedTerm > 0
  ) {
    const termInPeriodsCurrent =
      billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm;
    const calcRecurring = (pfrAmt - initialDownPmt) / termInPeriodsCurrent;
    if (customRecurringAmtValue < calcRecurring) {
      const minTermInPeriods = Math.ceil((pfrAmt - initialDownPmt) / customRecurringAmtValue);
      if (billingFrequency === 'weekly') {
        calculatedTerm = snapWeeksUpToAllowed(minTermInPeriods) / 4;
      } else {
        calculatedTerm = snapTermUpToAllowed(minTermInPeriods);
      }
    }
  }

  // When only affordable DP: ensure term is at least the minimum that keeps implied dp <= entered dp (already done above)
  if (
    showDownPayment &&
    !showRecurringAmount &&
    recurringAmt > 0 &&
    pfrAmt > initialDownPmt &&
    calculatedTerm > 0
  ) {
    const minTermDivisor = (pfrAmt - initialDownPmt) / recurringAmt;
    const minTermSnapped =
      billingFrequency === 'weekly'
        ? snapWeeksUpToAllowed(minTermDivisor) / 4
        : snapTermUpToAllowed(Math.ceil(minTermDivisor));
    if (calculatedTerm < minTermSnapped) {
      calculatedTerm = minTermSnapped;
    }
  }

  // If pfrAmt - dp - (term × recurring) < 0, reduce term so we stay within PFR (not down-payment-only — see above).
  // When user entered recurring we use that for the cap; else optimal (down-only) or (pfr-dp)/term.
  const termInPeriodsForCap =
    billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm;
  const recurringForCap =
    showRecurringAmount && customRecurringAmount
      ? customRecurringAmtValue
      : showDownPayment && !showRecurringAmount
        ? recurringAmt
        : calculatedTerm > 0 && pfrAmt > initialDownPmt
          ? (pfrAmt - initialDownPmt) / (billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm)
          : 0;
  const isUserEnteredRecurring = !!(showRecurringAmount && customRecurringAmount);
  const isDownPaymentOnly = showDownPayment && !showRecurringAmount;
  if (recurringForCap > 0 && !isUserEnteredRecurring) {
    const remainder = pfrAmt - initialDownPmt - termInPeriodsForCap * recurringForCap;
    // Down-payment-only: recurring may not exceed optimal. When term×optimal > remaining balance, lower recurring
    // (below optimal) — do not snap term down (e.g. 12→9), which would require recurring above optimal to balance.
    if (remainder < 0 && !isDownPaymentOnly) {
      const maxTermInPeriods = Math.floor((pfrAmt - initialDownPmt) / recurringForCap);
      if (billingFrequency === 'weekly') {
        calculatedTerm = snapWeeksDownToAllowed(maxTermInPeriods) / 4;
      } else {
        calculatedTerm = snapTermDownToAllowed(maxTermInPeriods);
      }
    }
  }

  const calculatedTermDisplay =
    billingFrequency === 'weekly' ? calculatedTerm * 4 : calculatedTerm;
  const calculatedTermDivisor = billingFrequency === 'weekly' ? calculatedTermDisplay : calculatedTerm;
  const customDownPmt = initialDownPmt;
  // When down payment only: recurring is at most optimal; use lower per-period amount when needed to balance.
  let customRecurringAmtFinal =
    showRecurringAmount && customRecurringAmount
      ? parseAmount(customRecurringAmount)
      : isDownPaymentOnly && calculatedTermDivisor > 0 && pfrAmt > customDownPmt
        ? Math.min(recurringAmt, (pfrAmt - customDownPmt) / calculatedTermDivisor)
        : isDownPaymentOnly
          ? recurringAmt
          : calculatedTermDivisor > 0 && pfrAmt > customDownPmt
            ? (pfrAmt - customDownPmt) / calculatedTermDivisor
            : 0;

  // Enforce pfrAmt - dp - (term × recurring) = 0 within tolerance by adjusting recurring (not down-payment-only:
  // there recurring is capped at optimal and balance is handled above).
  if (
    calculatedTermDivisor > 0 &&
    pfrAmt > customDownPmt &&
    !isDownPaymentOnly
  ) {
    const remainder = pfrAmt - customDownPmt - calculatedTermDivisor * customRecurringAmtFinal;
    if (Math.abs(remainder) > BALANCE_TOLERANCE) {
      customRecurringAmtFinal = (pfrAmt - customDownPmt) / calculatedTermDivisor;
    }
  }

  // Rule: calculated program down payment or recurring can never be greater than the PFR amount.
  const customDownPmtFinal = Math.min(customDownPmt, pfrAmt);
  const customRecurringAmtFinalClamped = Math.min(customRecurringAmtFinal, pfrAmt);

  const meetsPreauthMinimums = useMemo(() => {
    if (
      minDownPaymentPreapproved == null ||
      minRecurringPreapproved == null ||
      minTermPreapproved == null ||
      calculatedTerm <= 0
    ) {
      return false;
    }
    const minDown = minDownPaymentPreapproved;
    const maxTerm = minTermPreapproved;
    const minRec = minRecurringPreapprovedForPrompt ?? minRecurringPreapproved;
    const AMOUNT_TOLERANCE = 0.99;
    const dpAboveMinAndTermBelowMax =
      customDownPmtFinal > minDown && calculatedTerm < maxTerm;
    const recurringAboveMinAndTermBelowMax =
      customRecurringAmtFinalClamped > minRec && calculatedTerm < maxTerm;
    return (
      customDownPmtFinal >= minDown &&
      (dpAboveMinAndTermBelowMax ||
        recurringAboveMinAndTermBelowMax ||
        (calculatedTerm <= maxTerm &&
          customDownPmtFinal >= minDown - AMOUNT_TOLERANCE &&
          customRecurringAmtFinalClamped >= minRec - AMOUNT_TOLERANCE))
    );
  }, [
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    minRecurringPreapprovedForPrompt,
    minTermPreapproved,
    customDownPmtFinal,
    customRecurringAmtFinalClamped,
    calculatedTerm,
  ]);

  const isHighRisk = useMemo(() => {
    if (calculatedTerm <= 0) return false;
    if (matchingRule && matchingRule.ruleId > 0 && matchingRule.ruleId !== -1) {
      return !meetsPreauthMinimums;
    }
    if (calculatedTerm > 0 && (!matchingRule || matchingRule.ruleId === 0 || matchingRule.ruleId === -1)) {
      return true;
    }
    return false;
  }, [calculatedTerm, matchingRule, meetsPreauthMinimums]);

  const handleSelectCustomProgram = useCallback(async () => {
    if (programType === 'hre-approval' && !hreApprovalStatus) {
      dispatch(showErrorStatus('Please select an outcome.'));
      return;
    }

    const calculatedCustomDownPmt = Math.min(
      showDownPayment && customDownPayment
        ? parseAmount(customDownPayment)
        : showRecurringAmount && !showDownPayment
          ? customDownPmtFinal
          : optDownPayment || 0,
      pfrAmt
    );
    const customRecurringAmtVal =
      showRecurringAmount && customRecurringAmount ? parseAmount(customRecurringAmount) : 0;
    let termForProgram: number;
    let recurringAmtForProgram: number;
    if (showDownPayment && !showRecurringAmount) {
      termForProgram = calculatedTerm;
      recurringAmtForProgram = customRecurringAmtFinalClamped;
    } else {
      termForProgram = parseFloat(customTerm) || 0;
      if (termForProgram === 0 && customRecurringAmtVal > 0 && pfrAmt > calculatedCustomDownPmt) {
        const termInPeriods = Math.floor((pfrAmt - calculatedCustomDownPmt) / customRecurringAmtVal);
        termForProgram =
          billingFrequency === 'weekly' ? Math.ceil(termInPeriods / 4) : termInPeriods;
      }
      const effectiveTermForProgram =
        billingFrequency === 'weekly' ? termForProgram * 4 : termForProgram;
      recurringAmtForProgram = Math.min(
        effectiveTermForProgram > 0 && pfrAmt > calculatedCustomDownPmt
          ? (pfrAmt - calculatedCustomDownPmt) / effectiveTermForProgram
          : customRecurringAmtVal > 0
            ? customRecurringAmtVal
            : 0,
        pfrAmt
      );
    }
    const isReject =
      programType === 'hre-approval' &&
      (hreApprovalStatus === 'rejected-by-client' || hreApprovalStatus === 'rejected-by-guarantor');
    createProgramAndNavigate(
      {
        isPaidInFull: false,
        downPmtAmt: calculatedCustomDownPmt,
        isHighRisk,
        terms: termForProgram,
        recurringAmt: recurringAmtForProgram,
      },
      isReject ? { navigateTo: 'ioc' } : undefined
    );
  }, [
    programType,
    hreApprovalStatus,
    dispatch,
    showDownPayment,
    customDownPayment,
    showRecurringAmount,
    customRecurringAmount,
    customTerm,
    calculatedTerm,
    customRecurringAmtFinalClamped,
    customDownPmtFinal,
    optDownPayment,
    pfrAmt,
    isHighRisk,
    billingFrequency,
    createProgramAndNavigate,
  ]);

  const handleProcessHre = useCallback(() => {
    const calculatedCustomDownPmt = Math.min(
      showDownPayment && customDownPayment
        ? parseAmount(customDownPayment)
        : showRecurringAmount && !showDownPayment
          ? customDownPmtFinal
          : optDownPayment || 0,
      pfrAmt
    );
    const customRecurringAmtVal =
      showRecurringAmount && customRecurringAmount ? parseAmount(customRecurringAmount) : 0;
    let termForProgram: number;
    let recurringAmtForProgram: number;
    if (showDownPayment && !showRecurringAmount) {
      termForProgram = calculatedTerm;
      recurringAmtForProgram = customRecurringAmtFinalClamped;
    } else {
      termForProgram = parseFloat(customTerm) || 0;
      if (termForProgram === 0 && customRecurringAmtVal > 0 && pfrAmt > calculatedCustomDownPmt) {
        const termInPeriods = Math.floor((pfrAmt - calculatedCustomDownPmt) / customRecurringAmtVal);
        termForProgram =
          billingFrequency === 'weekly' ? Math.ceil(termInPeriods / 4) : termInPeriods;
      }
      const effectiveTermForProgram =
        billingFrequency === 'weekly' ? termForProgram * 4 : termForProgram;
      recurringAmtForProgram = Math.min(
        effectiveTermForProgram > 0 && pfrAmt > calculatedCustomDownPmt
          ? (pfrAmt - calculatedCustomDownPmt) / effectiveTermForProgram
          : customRecurringAmtVal > 0
            ? customRecurringAmtVal
            : 0,
        pfrAmt
      );
    }
    createProgramAndNavigate(
      {
        isPaidInFull: false,
        downPmtAmt: calculatedCustomDownPmt,
        isHighRisk: true,
        terms: termForProgram,
        recurringAmt: recurringAmtForProgram,
      },
      { navigateTo: 'hre-approval' }
    );
  }, [
    showDownPayment,
    customDownPayment,
    showRecurringAmount,
    customRecurringAmount,
    customTerm,
    calculatedTerm,
    customRecurringAmtFinalClamped,
    customDownPmtFinal,
    optDownPayment,
    pfrAmt,
    billingFrequency,
    createProgramAndNavigate,
  ]);

  const handleSelectMinPreapprovedProgram = useCallback(() => {
    if (
      minDownPaymentPreapproved == null ||
      minRecurringPreapproved == null ||
      minTermPreapproved == null
    ) {
      return;
    }
    createProgramAndNavigate({
      isPaidInFull: false,
      downPmtAmt: minDownPaymentPreapproved,
      isHighRisk: false,
      terms: minTermPreapproved,
      recurringAmt: minRecurringPreapproved,
    });
  }, [
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    minTermPreapproved,
    createProgramAndNavigate,
  ]);

  const addition: CustomProgramContextAddition = {
    customDownPayment,
    setCustomDownPayment,
    customTerm,
    setCustomTerm,
    customRecurringAmount,
    setCustomRecurringAmount,
    downPaymentError,
    recurringAmountError,
    isHighRisk,
    meetsPreauthMinimums,
    minDownPaymentPreapproved,
    minRecurringPreapproved,
    minRecurringPreapprovedForPrompt,
    minTermPreapproved,
    customDownPmt: customDownPmtFinal,
    customTermValue,
    customRecurringAmtValue,
    calculatedTerm,
    calculatedTermDisplay,
    customRecurringAmtFinal: customRecurringAmtFinalClamped,
    handleDownPaymentChange,
    handleRecurringAmountChange,
    handleDownPaymentBlur,
    handleRecurringAmountBlur,
    handleSelectCustomProgram,
    handleSelectMinPreapprovedProgram,
    handleProcessHre,
  };
  return addition;
}
